import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const TrustScoreWidget = ({ userProfile }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Calculate trust score based on various factors
  const calculateTrustScore = () => {
    let score = 0;
    let maxScore = 100;

    // Verification factors (40 points)
    if (userProfile?.emailVerified) score += 10;
    if (userProfile?.phoneVerified) score += 10;
    if (userProfile?.photoVerified) score += 10;
    if (userProfile?.identityVerified) score += 10;

    // Profile completeness (20 points)
    if (userProfile?.bio && userProfile?.bio?.length > 50) score += 10;
    if (userProfile?.interests && userProfile?.interests?.length >= 3) score += 10;

    // Activity factors (20 points)
    const accountAge = userProfile?.createdAt ? Math.floor((Date.now() - new Date(userProfile?.createdAt)?.getTime()) / (1000 * 60 * 60 * 24)) : 0;
    if (accountAge > 30) score += 10;
    if (accountAge > 90) score += 10;

    // Community standing (20 points)
    const reportCount = userProfile?.reportCount || 0;
    if (reportCount === 0) score += 20;
    else if (reportCount <= 2) score += 10;

    return Math.min(score, maxScore);
  };

  const trustScore = calculateTrustScore();

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-primary';
    if (score >= 40) return 'text-warning';
    return 'text-error';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-success';
    if (score >= 60) return 'bg-primary';
    if (score >= 40) return 'bg-warning';
    return 'bg-error';
  };

  const trustFactors = [
    {
      label: 'Verifications',
      value: [userProfile?.emailVerified, userProfile?.phoneVerified, userProfile?.photoVerified, userProfile?.identityVerified]?.filter(Boolean)?.length,
      max: 4,
      icon: 'CheckCircle'
    },
    {
      label: 'Profile Quality',
      value: (userProfile?.bio?.length > 50 ? 1 : 0) + (userProfile?.interests?.length >= 3 ? 1 : 0),
      max: 2,
      icon: 'User'
    },
    {
      label: 'Community Standing',
      value: (userProfile?.reportCount || 0) === 0 ? 2 : (userProfile?.reportCount || 0) <= 2 ? 1 : 0,
      max: 2,
      icon: 'Shield'
    }
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Trust Score</h2>
        <button
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="relative"
        >
          <Icon name="Info" size={18} className="text-muted-foreground hover:text-foreground" />
          {showTooltip && (
            <div className="absolute right-0 top-6 w-64 p-3 bg-popover border border-border rounded-lg shadow-lg z-10">
              <p className="text-xs text-popover-foreground">
                Your trust score is calculated based on verifications, profile completeness, account age, and community standing. Higher scores increase visibility and trust.
              </p>
            </div>
          )}
        </button>
      </div>

      {/* Score Display */}
      <div className="flex flex-col items-center mb-6">
        <div className="relative w-32 h-32 mb-3">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              className="text-muted"
            />
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 56}`}
              strokeDashoffset={`${2 * Math.PI * 56 * (1 - trustScore / 100)}`}
              className={cn("transition-all duration-1000", getScoreColor(trustScore))}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className={cn("text-3xl font-bold", getScoreColor(trustScore))}>{trustScore}</div>
            <div className="text-xs text-muted-foreground">out of 100</div>
          </div>
        </div>
        <div className={cn("px-3 py-1 rounded-full text-sm font-medium", `${getScoreBgColor(trustScore)}/20`, getScoreColor(trustScore))}>
          {getScoreLabel(trustScore)}
        </div>
      </div>

      {/* Trust Factors */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-foreground">Score Breakdown</h3>
        {trustFactors?.map((factor, index) => (
          <div key={index} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name={factor?.icon} size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground">{factor?.label}</span>
              </div>
              <span className="text-sm font-medium text-muted-foreground">
                {factor?.value}/{factor?.max}
              </span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className={cn("h-full transition-all duration-500", getScoreBgColor(trustScore))}
                style={{ width: `${(factor?.value / factor?.max) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {trustScore < 80 && (
        <div className="mt-6 p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <p className="text-xs text-foreground font-medium mb-1">Improve your score</p>
          <p className="text-xs text-muted-foreground">
            Complete more verifications and maintain positive community interactions to increase your trust score.
          </p>
        </div>
      )}
    </div>
  );
};

export default TrustScoreWidget;